/**
 * View Total / Advance workstation — Backend-UI `advance-workstation/:id`.
 * Stacks one Detail Book panel per comma-joined market id (match odds + bookmakers).
 * Fancy/PB excluded from View Total id list (Backend-UI `getUrl` parity).
 *
 * Live: same Lab APIs as Detail Book via `useWorkstationBook` (no CM Betfair strip).
 * Live odds: Socket-API SSE per panel (Lab rateSocket). Demo / no bet URL → fixture.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useThemePrefs } from '@/app/theme'
import {
  useWorkstationBook,
  type WorkstationBetFilters,
} from '@/api/hooks/net-exposure/useWorkstation'
import { BOOK_TYPES, type BookType } from './fixture'
import {
  defaultBetDateRange,
  marketTitleFor,
  parseAdvanceMarketIds,
  parseWorkstationSearch,
} from './workstation-fixture'
import { BetBreakdown } from './workstation/BetBreakdown'
import { BookTree } from './workstation/BookTree'
import { OddsPanel } from './workstation/OddsPanel'
import './workstation/workstation.css'

function MarketPanel({
  marketId,
  index,
  bookType,
  onBookType,
  bookSurface,
  eventFallback,
}: {
  marketId: string
  index: number
  bookType: BookType
  onBookType: (next: BookType) => void
  bookSurface: 'dark' | 'light'
  eventFallback: string
}) {
  const [open, setOpen] = useState(true)
  const {
    book,
    isLoading,
    isError,
    error,
    live,
  } = useWorkstationBook({
    marketId,
    selectedType: bookType,
    eventName: eventFallback,
  })

  const type = book?.type ?? (marketId.includes('#') ? 'bookmaker' : 'match')
  const marketName = book?.marketName ?? marketId
  const title = marketTitleFor(type, marketName)
  const radioName = `w7-adv-book-${index}`

  return (
    <div className="w7-ws-adv-panel" data-open={open ? 'true' : 'false'}>
      <div className="w7-ws-market-bar w7-ws-adv-bar">
        <button
          type="button"
          className="w7-ws-adv-toggle"
          aria-expanded={open}
          aria-controls={`w7-adv-body-${index}`}
          onClick={() => setOpen((v) => !v)}
          title={open ? 'Collapse' : 'Expand'}
        >
          {open ? '−' : '+'}
        </button>
        <h2 className="w7-ws-market-title">{title}</h2>
        <div className="w7-ws-book-radios" role="radiogroup" aria-label={`${title} book type`}>
          {BOOK_TYPES.map((typeOption) => (
            <label key={typeOption} className="w7-ws-book-radio">
              <input
                type="radio"
                name={radioName}
                value={typeOption}
                checked={bookType === typeOption}
                onChange={() => onBookType(typeOption)}
              />
              <span>{typeOption}</span>
            </label>
          ))}
        </div>
      </div>

      {open ? (
        isLoading ? (
          <p className="w7-ws-empty">Loading market…</p>
        ) : isError ? (
          <p className="w7-ws-empty">
            Could not load books
            {error instanceof Error && error.message ? `: ${error.message}` : ''}
          </p>
        ) : (
          <div className="w7-ws-body" id={`w7-adv-body-${index}`}>
            <section
              className="w7-ws-odds-card w7-ws-book"
              data-ws-surface={bookSurface}
              aria-label={`${title} odds`}
            >
              {book ? (
                <OddsPanel book={book} bookType={bookType} />
              ) : (
                <p className="w7-ws-empty">
                  {live ? 'No open book for this market.' : `No fixture for market ${marketId}.`}
                </p>
              )}
            </section>
            <section
              className="w7-ws-ne-card w7-ws-book"
              data-ws-surface={bookSurface}
              aria-label={`${title} net exposure`}
            >
              {book ? (
                <BookTree book={book} bookType={bookType} live={live} />
              ) : (
                <p className="w7-ws-empty">
                  {live ? 'No open book for this market.' : `No fixture for market ${marketId}.`}
                </p>
              )}
            </section>
          </div>
        )
      ) : null}

      {!book && open && !isLoading && !live ? (
        <p className="w7-ws-adv-hint">Event context: {eventFallback}</p>
      ) : null}
    </div>
  )
}

export function AdvanceWorkstationPage() {
  const { id = '' } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { theme, neCards } = useThemePrefs()
  const shellSurface = theme
  /** Odds + hierarchy cards follow Net Exposure cards toggle (Agents parity). */
  const bookSurface = neCards
  const parsed = useMemo(() => parseWorkstationSearch(searchParams), [searchParams])
  const bookType = parsed.book

  const marketIds = useMemo(() => parseAdvanceMarketIds(id), [id])
  const primaryId = marketIds[0] ?? ''
  const betRange = useMemo(() => defaultBetDateRange(), [])
  const [betFilters, setBetFilters] = useState<WorkstationBetFilters>(() => ({
    page: 1,
    maxRow: 100,
    fromDate: betRange.fromDate,
    toDate: betRange.toDate,
    currencyType: 'All',
  }))
  const {
    book: primaryBook,
    live: primaryLive,
    betCount,
  } = useWorkstationBook({
    marketId: primaryId,
    selectedType: bookType,
    eventName: parsed.event,
    ...betFilters,
  })
  const eventName =
    parsed.event ||
    primaryBook?.eventName ||
    'Event'

  /** Backend-UI: one shared book type for panels on advance (each panel has radios; we sync). */
  const setBook = (next: BookType) => {
    const params = new URLSearchParams(searchParams)
    params.set('book', next)
    setSearchParams(params, { replace: true })
  }

  return (
    <div className="w7-ws w7-ws-adv" data-ws-surface={shellSurface}>
      <header className="w7-ws-header">
        <button
          type="button"
          className="w7-ws-back"
          aria-label="Back to Net Exposure"
          onClick={() => navigate('/book-management/net-exposure')}
        >
          ‹
        </button>
        <h1 className="w7-ws-event">{eventName}</h1>
      </header>

      {marketIds.length === 0 ? (
        <p className="w7-ws-empty">No markets in View Total.</p>
      ) : (
        marketIds.map((marketId, index) => (
          <MarketPanel
            key={`${marketId}-${index}`}
            marketId={marketId}
            index={index}
            bookType={bookType}
            onBookType={setBook}
            bookSurface={bookSurface}
            eventFallback={eventName}
          />
        ))
      )}

      {/* Backend-UI bet-breakdown uses marketIds[0] only */}
      {primaryBook ? (
        <BetBreakdown
          book={primaryBook}
          surface={shellSurface}
          live={primaryLive}
          betCount={betCount}
          betFilters={primaryLive ? betFilters : undefined}
          onBetFiltersChange={primaryLive ? setBetFilters : undefined}
        />
      ) : null}
    </div>
  )
}
